import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import type { Client } from "@shared/schema";
import { ClientService } from "@/services/ClientService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

// Form Schema
const formSchema = z.object({
    name: z.string().min(2, "Name must be at least 2 characters"),
    description: z.string().optional(),
    billingName: z.string().optional(),
    billingAddress: z.string().optional(),
    vatNumber: z.string().optional(),
    paymentTerms: z.string().optional(),
    primaryContactName: z.string().optional(),
    primaryContactEmail: z.string().email("Invalid email").optional().or(z.literal("")),
    primaryContactPhone: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ClientDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    client?: Client | null; // If present, edit mode
}

export function ClientDialog({ open, onOpenChange, client }: ClientDialogProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const isEditing = !!client;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            description: "",
            billingName: "",
            billingAddress: "",
            vatNumber: "",
            paymentTerms: "",
            primaryContactName: "",
            primaryContactEmail: "",
            primaryContactPhone: "",
        },
    });

    // Reset form when opening or changing client
    useEffect(() => {
        if (client) {
            form.reset({
                name: client.name,
                description: client.description || "",
                billingName: client.billingName || "",
                billingAddress: client.billingAddress || "",
                vatNumber: client.vatNumber || "",
                paymentTerms: client.paymentTerms || "",
                primaryContactName: client.primaryContactName || "",
                primaryContactEmail: client.primaryContactEmail || "",
                primaryContactPhone: client.primaryContactPhone || "",
            });
        } else {
            form.reset({
                name: "",
                description: "",
                billingName: "",
                billingAddress: "",
                vatNumber: "",
                paymentTerms: "",
                primaryContactName: "",
                primaryContactEmail: "",
                primaryContactPhone: "",
            });
        }
    }, [client, open, form]);

    const createMutation = useMutation({
        mutationFn: ClientService.createClient,
        onSuccess: (response) => {
            toast({
                title: "Success",
                description: response.message,
            });
            queryClient.invalidateQueries({ queryKey: ["clients"] });
            queryClient.invalidateQueries({ queryKey: ["clients-list-simple"] }); // For AdvertiserForm dropdown
            onOpenChange(false);
        },
        onError: (error: any) => {
            toast({
                title: "Error",
                description: error.message || "Failed to create client",
                variant: "destructive",
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: (data: FormValues) => ClientService.updateClient(client!.id, data),
        onSuccess: (response) => {
            toast({
                title: "Success",
                description: response.message,
            });
            queryClient.invalidateQueries({ queryKey: ["clients"] });
            queryClient.invalidateQueries({ queryKey: ["clients-list-simple"] }); // For AdvertiserForm dropdown
            queryClient.invalidateQueries({ queryKey: ["client", client!.id] });
            onOpenChange(false);
        },
        onError: (error: any) => {
            toast({
                title: "Error",
                description: error.message || "Failed to update client",
                variant: "destructive",
            });
        },
    });

    const onSubmit = (values: FormValues) => {
        // Clean up empty strings to undefined for optional fields if needed, 
        // but the backend handles strings generally well.
        // Ensure email is not empty string if optional
        const apiValues = { ...values };
        if (!apiValues.primaryContactEmail) delete apiValues.primaryContactEmail;

        if (isEditing) {
            updateMutation.mutate(apiValues);
        } else {
            createMutation.mutate(apiValues as any);
        }
    };

    const isPending = createMutation.isPending || updateMutation.isPending;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isEditing ? "Edit Client" : "Create New Client"}</DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? "Update client details and commercial information."
                            : "Add a new commercial counterparty to the system."}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem className="col-span-2">
                                        <FormLabel>Client Name *</FormLabel>
                                        <FormControl>
                                            <Input placeholder="e.g. Acme Corporation" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem className="col-span-2">
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Textarea placeholder="Internal notes about this client..." {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Billing Info Section */}
                            <div className="col-span-2 pt-2 border-t mt-2">
                                <h3 className="font-medium mb-2 text-sm text-gray-900">Billing Information</h3>
                            </div>

                            <FormField
                                control={form.control}
                                name="billingName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Billing Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Legal entity name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="vatNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>VAT Number</FormLabel>
                                        <FormControl>
                                            <Input placeholder="e.g. IE1234567T" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="billingAddress"
                                render={({ field }) => (
                                    <FormItem className="col-span-2">
                                        <FormLabel>Billing Address</FormLabel>
                                        <FormControl>
                                            <Textarea placeholder="Full billing address" {...field} rows={3} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="paymentTerms"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Payment Terms</FormLabel>
                                        <FormControl>
                                            <Input placeholder="e.g. Net 30" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Contact Info Section */}
                            <div className="col-span-2 pt-2 border-t mt-2">
                                <h3 className="font-medium mb-2 text-sm text-gray-900">Primary Contact</h3>
                            </div>

                            <FormField
                                control={form.control}
                                name="primaryContactName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Contact Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Key account manager" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="primaryContactEmail"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email</FormLabel>
                                        <FormControl>
                                            <Input type="email" placeholder="contact@example.com" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="primaryContactPhone"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Phone</FormLabel>
                                        <FormControl>
                                            <Input placeholder="+353..." {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isPending}>
                                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {isEditing ? "Save Changes" : "Create Client"}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
